import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Route, NavLink, useLocation, useHistory, Switch } from 'react-router-dom';
import { DateTime } from 'luxon';

import Button from '@material-ui/core/Button';
import MenuList from '@material-ui/core/MenuList';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemText from '@material-ui/core/ListItemText';

import ElectionDetails from 'app/components/election/ElectionDetails';
import ProposalDetails from 'app/components/election/ProposalDetails';
import FormBuilder from 'app/components/form/FormBuilder';
import RecommendationDetails from 'app/components/election/RecommendationDetails';
import VotingDetails from 'app/components/election/VotingDetails';
import OtherDetails from 'app/components/election/OtherDetails';

import NewElectionDialog from 'app/components/election/NewElectionDialog';
import CopyElectionDialog from 'app/components/election/CopyElectionDialog';
import RedButton from 'app/components/buttons/RedButton';
import useApi from 'app/context/useApi';

import { containerFlex, containerFlexFixedSmall, containerFlexGrow} from 'app/styles/new-layout';

const useStyles = makeStyles(theme => ({
    container: {
        ...containerFlex(theme),
        padding: theme.spacing(3)
    },
    nav: {
        ...containerFlexFixedSmall(theme),
    },
    content: {
        ...containerFlexGrow(theme),
        paddingLeft: theme.spacing(3),
        minHeight: 600,

        [theme.breakpoints.down('sm')]: {
            paddingLeft: '0px',
        }
    },
    marginBottomSmall: {
        marginBottom: theme.spacing(1)
    },
    menuItem: {
        borderRadius: theme.shape.borderRadius
    },
    saveButtonContainer: {
        marginTop: theme.spacing(3)
    },
    saveButton: {
        paddingLeft: theme.spacing(5),
        paddingRight: theme.spacing(5),
    },
}));

const initFieldOptions = {
    group_id: null,
    type: 'textbox',
    label: '',
    options: [],
    length: 1,
    strict_select: false,
    required: false,
    title: false,
    subtitle: false,
    viewable_for_recommendations: false,
    viewable_for_voting: false,
    align: 'left',
    helper_text: '',
    user_id: null
}

export default function Settings(props) {

    const { loading, setLoading, groups, getElection } = props;

    let location = useLocation();
    const history = useHistory();
    const classes = useStyles();
    const api = useApi();

    const [ election, setElection ] = useState(props.election);
    const [ fields, setFields ] = useState(props.election.fields);
    const [ errors, setErrors ] = useState({});

    const [ copyElectionOptions, setCopyElectionOptions ] = useState({});
    const [ copyElectionDialogOpen, setCopyElectionDialogOpen ] = useState(false);
    const [ newElectionDialogOpen, setNewElectionDialogOpen ] = useState(false);

    const electionId = props.election.id;
    const pathname = location.pathname;

    // handlers ------------------------------------------------

    const handleElection = name => action => {

        let value;

        if (DateTime.isDateTime(action) || action === null) {
            value = action;
        } else {
            const target = action.target;
            value = (target.type === 'checkbox' ? target.checked : target.value);
        }

        if (isError(name)) delete errors[name];

        setElection({ ...election, [name]: value });
    };

    // Post Function ------------------------------------------

    const postElection = () => {

        setLoading(true)

        const data = {...election, fields}

        if (election.starts_at) data.starts_at = election.starts_at.toSeconds();
        if (election.proposals_start_at) data.proposals_start_at = election.proposals_start_at.toSeconds();
        if (election.proposals_end_at) data.proposals_end_at = election.proposals_end_at.toSeconds();
        if (election.recommendations_start_at) data.recommendations_start_at = election.recommendations_start_at.toSeconds();
        if (election.recommendations_end_at) data.recommendations_end_at = election.recommendations_end_at.toSeconds();
        if (election.voting_starts_at) data.voting_starts_at = election.voting_starts_at.toSeconds();
        if (election.voting_ends_at) data.voting_ends_at = election.voting_ends_at.toSeconds();

        api.post(`/admin/election/${electionId}`, data)
            .then(() => getElection())
            .catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                    setLoading(false)
                }
            });
    }

    const deleteElection = () => {

        setLoading(true);

        api.delete(`/admin/election/${electionId}`)
            .then(() => history.push('/admin/elections'))
            .catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
                setLoading(false);
            });
    }

    // Errors ---------------------------------------------------

    const isError = field => {
        if (errors[field]) return true;
        return false;
    }

    const getErrorMessage = field => {
        if (isError(field)) return errors[field];
        return "";
    }

    // Dialogs ----------------------------------------------

    const openNewElectionDialog = () => {
        setNewElectionDialogOpen(true)
    }

    const closeNewElectionDialog = () => {
        setNewElectionDialogOpen(false);
    }

    const openCopyElectionDialog = () => {
        setCopyElectionDialogOpen(true)
    }

    const closeCopyElectionDialog = () => {
        setCopyElectionDialogOpen(false);
    }

    const handleCopyElection = (vars) => {
        console.log(vars);
        setCopyElectionOptions(vars);
        openNewElectionDialog(true);
    }

    // Constants ---------------------------------------------------

    const isSelected = tab => (tab === pathname);

    return (
        <div className={classes.container}>
            <div className={classes.nav}>
                <MenuList>
                    <MenuItem
                        to={`/admin/elections/${electionId}/settings`}
                        className={classes.menuItem}
                        selected={isSelected(`/admin/elections/${electionId}/settings`)}
                        component={NavLink}
                        >
                        <ListItemText primary="Election" />
                    </MenuItem>
                    <MenuItem
                        to={`/admin/elections/${electionId}/settings/proposals`}
                        className={classes.menuItem}
                        selected={isSelected(`/admin/elections/${electionId}/settings/proposals`)}
                        component={NavLink}
                        >
                        <ListItemText primary="Proposals" />
                    </MenuItem>
                    <MenuItem
                        to={`/admin/elections/${electionId}/settings/form`}
                        className={classes.menuItem}
                        selected={isSelected(`/admin/elections/${electionId}/settings/form`)}
                        component={NavLink}
                        >
                        <ListItemText primary="Form Builder" />
                    </MenuItem>
                    <MenuItem
                        to={`/admin/elections/${electionId}/settings/recommendations`}
                        className={classes.menuItem}
                        selected={isSelected(`/admin/elections/${electionId}/settings/recommendations`)}
                        component={NavLink}
                        >
                        <ListItemText primary="Recommendations" />
                    </MenuItem>
                    <MenuItem
                        to={`/admin/elections/${electionId}/settings/voting`}
                        className={classes.menuItem}
                        selected={isSelected(`/admin/elections/${electionId}/settings/voting`)}
                        component={NavLink}
                        >
                        <ListItemText primary="Voting" />
                    </MenuItem>
                    <MenuItem
                        to={`/admin/elections/${electionId}/settings/other`}
                        className={classes.menuItem}
                        selected={isSelected(`/admin/elections/${electionId}/settings/other`)}
                        component={NavLink}
                        >
                        <ListItemText primary="Other" />
                    </MenuItem>
                </MenuList>
                <Button
                    disabled={loading}
                    variant="contained"
                    color="primary"
                    onClick={postElection}
                    className={classes.marginBottomSmall}
                    fullWidth
                    >
                    save
                </Button>
                <Button
                    fullWidth
                    variant="outlined"
                    onClick={openCopyElectionDialog}
                    className={classes.marginBottomSmall}
                    >
                    Copy Election
                </Button>
                <RedButton
                    fullWidth
                    variant="outlined"
                    onClick={deleteElection}
                    >
                    Delete Election
                </RedButton>
            </div>
            <div className={classes.content}>
                <Switch>
                    <Route path={`/admin/elections/${electionId}/settings`} exact>
                        <ElectionDetails
                            election={election}
                            handleElection={handleElection}
                            isError={isError}
                            getErrorMessage={getErrorMessage}
                        />
                    </Route>
                    <Route path={`/admin/elections/${electionId}/settings/proposals`} exact>
                        <ProposalDetails
                            election={election}
                            handleElection={handleElection}
                            fields={fields}
                            isError={isError}
                            getErrorMessage={getErrorMessage}
                            groups={groups}
                        />
                    </Route>
                    <Route path={`/admin/elections/${electionId}/settings/form`} exact>
                        <FormBuilder
                            fields={fields}
                            setFields={setFields}
                            errors={errors}
                            type='election'
                            election={election}
                        />
                    </Route>
                    <Route path={`/admin/elections/${electionId}/settings/recommendations`} exact>
                        <RecommendationDetails
                            election={election}
                            handleElection={handleElection}
                            isError={isError}
                            getErrorMessage={getErrorMessage}
                            groups={groups}
                        />
                    </Route>
                    <Route path={`/admin/elections/${electionId}/settings/voting`} exact>
                        <VotingDetails
                            election={election}
                            handleElection={handleElection}
                            isError={isError}
                            getErrorMessage={getErrorMessage}
                            groups={groups}
                        />
                    </Route>
                    <Route path={`/admin/elections/${electionId}/settings/other`} exact>
                        <OtherDetails
                            election={election}
                            handleElection={handleElection}
                            isError={isError}
                            getErrorMessage={getErrorMessage}
                            groups={groups}
                        />
                    </Route>
                </Switch>
            </div>
            <CopyElectionDialog
                open={copyElectionDialogOpen}
                onClose={closeCopyElectionDialog}
                handleCopyElection={handleCopyElection}
            />
            <NewElectionDialog
                open={newElectionDialogOpen}
                onClose={closeNewElectionDialog}
                copy={election}
                copyFields={fields}
                copyOptions={copyElectionOptions}
            />
        </div>
    );
}
